import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, noop } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { BuildPriceEntities, BuildPriceModel, BuildPricePaths } from 'client/data/models/build-price';
import { buildPopularStylesPath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from 'client/data/models/unified-lead-form';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { buildOptionsString } from 'site-modules/shared/utils/build-price/build-price';
import {
  getLeadSubmissionInfoWithoutEmail,
  getVehicleProps,
} from 'site-modules/shared/components/lead-form/utils/utils';
import { getBuildPriceCustomConfig } from 'site-modules/build-price/components/build-price-lead-form/build-price-lead-form-utils';
import { sendMarketoThankYouEmail } from 'site-modules/shared/components/lead-form/lead-form-wrapper/lead-form-wrapper-methods';
import { UnifiedLeadFormAsync } from 'site-modules/shared/components/lead-form/unified-lead-form/unified-lead-form-async';
import { TransparentPricingLeadForm } from 'site-modules/shared/components/lead-form/unified-lead-form-pages/transparent-pricing-lead-form';

function BuildPriceLeadFormUI({
  optionsList = null,
  buildPriceFilter,
  style = null,
  tmv = null,
  leadFormDealerCount = null,
  onSummaryLeadFormSubmit = noop,
  styleId = null,
  options = {},
  pathname = null,
  zipCode = null,
  isTransparentPricing = false,
}) {
  const optionIds = get(buildPriceFilter, 'optionIds');
  const getLeadSubmissionInfoWithComments = useCallback(
    params => {
      const submissionInfo = getLeadSubmissionInfoWithoutEmail(params);

      return {
        ...submissionInfo,
        leads: submissionInfo.leads.map(lead => ({
          ...lead,
          comments: buildOptionsString(optionsList, optionIds),
        })),
      };
    },
    [optionIds, optionsList]
  );

  if (!(style && leadFormDealerCount && tmv)) {
    return null;
  }

  const { makeName, makeSlug, modelName, modelSlug, year, trim, publicationState } = style;
  const {
    styleFilter: { submodel },
  } = buildPriceFilter;
  const { creativeId, headerText, isMobile, dataTrackingParent } = options;

  const leadFormVehicle = {
    make: { name: makeName, slug: makeSlug },
    model: { name: modelName, slug: modelSlug },
    submodels: { name: submodel, slug: makeNiceName(submodel) },
    year,
    publicationState,
  };

  const srpLink = srpLinkBuilder({
    make: makeSlug,
    model: modelSlug,
    year,
    trim,
    inventorytype: 'new',
  });
  const bestDealPrice = get(tmv, 'totalWithOptions.tmv') - get(tmv, 'incentivesAndRebates');

  return (
    <div className="mt-1" data-tracking-parent={dataTrackingParent}>
      {isTransparentPricing ? (
        <TransparentPricingLeadForm
          params={{
            vehicle: leadFormVehicle,
            styleId,
            trim,
            zipCode,
            srpLink,
          }}
          options={{
            bestDealPrice,
            isMobile,
            creativeId: creativeId || CREATIVE_ID.NCI_ELF_PRICING_BUILD,
            processDataMethods: {
              getLeadSubmissionInfo: getLeadSubmissionInfoWithComments,
            },
            headingTag: 'h2',
            isBuildPriceAdEnabled: true,
          }}
        />
      ) : (
        <UnifiedLeadFormAsync
          {...getVehicleProps()(leadFormVehicle)}
          styleId={styleId}
          trim={trim}
          zipCode={zipCode}
          customConfig={getBuildPriceCustomConfig({
            creativeId: creativeId || CREATIVE_ID.NCI_ELF_PRICING_BUILD,
            srpLink,
            headerText,
            isMobile,
            processDataMethods: {
              getLeadSubmissionInfo: getLeadSubmissionInfoWithComments,
              postProcessMethods: [sendMarketoThankYouEmail, onSummaryLeadFormSubmit],
            },
            bestDealPrice,
          })}
          pathname={pathname}
          radius={100}
          config={LEAD_FORM_CONFIGS.QUICK_QUOTES_ROI}
        />
      )}
    </div>
  );
}

BuildPriceLeadFormUI.propTypes = {
  modelYear: BuildPriceEntities.ModelYear.isRequired,
  buildPriceFilter: BuildPriceEntities.BuildPriceFilter.isRequired,
  tmv: BuildPriceEntities.Tmv,
  style: VehicleEntities.Style,
  leadFormDealerCount: PropTypes.number,
  onSummaryLeadFormSubmit: PropTypes.func,
  optionsList: PropTypes.arrayOf(BuildPriceEntities.Option),
  styleId: PropTypes.string,
  pathname: PropTypes.string,
  options: PropTypes.shape({
    creativeId: PropTypes.string,
    dataTrackingParent: PropTypes.string,
    headerText: PropTypes.string,
    isMobile: PropTypes.bool,
  }),
  zipCode: PropTypes.string,
  isTransparentPricing: PropTypes.bool,
};

const mapStateToProps = state => ({
  pathname: get(state, 'pageContext.location.pathname'),
  zipCode: get(state, 'visitor.location.zipCode'),
  isMobile: get(state, 'mobile'),
});

const stateToPropsConfig = {
  leadFormDealerCount: bindToPath(
    ({ modelYear: { makeSlug, modelSlug }, isTransparentPricing }) =>
      getUnifiedLeadFormDealerCountV2Path({
        makeSlug,
        modelSlug,
        strategy: isTransparentPricing
          ? LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy
          : LEAD_FORM_CONFIGS.QUICK_QUOTES_ROI.strategy,
      }),
    UnifiedLeadFormModel
  ),
  style: bindToPath(
    ({ modelYear: { makeSlug, modelSlug, year } }) =>
      buildPopularStylesPath({ make: makeSlug, model: modelSlug, year }),
    VehicleModel,
    (styles, { buildPriceFilter: { styleId } }) => styles.find(({ id }) => id === parseInt(styleId, 10))
  ),
  optionsList: bindToPath(
    ({ buildPriceFilter: { styleId } }) => BuildPricePaths.buildStyleOptionsPath(styleId),
    BuildPriceModel
  ),
  tmv: bindToPath(
    ({ buildPriceFilter: { styleId, optionIds } }) =>
      BuildPricePaths.buildConfigurationTmvWithOptionsPath(styleId, optionIds),
    BuildPriceModel
  ),
};

export const BuildPriceLeadForm = connect(mapStateToProps)(connectToModel(BuildPriceLeadFormUI, stateToPropsConfig));
